'use client';

import { c as _c } from "react/compiler-runtime";
import type { User } from '@supabase/supabase-js';
import type { ReactNode } from 'react';
import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
import { clearAllCaches, clearQueryCache, setQueryCacheOwner } from '@/components/providers/query-provider';
import { clientLogger } from '@/lib/client-logger';
import { createClient, syncRealtimeAuth } from '@/utils/supabase/client';
type AuthState = {
  user: User | null;
  error: Error | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
};

// Default context state
const initialState: AuthState = {
  user: null,
  error: null,
  isLoading: true,
  isAuthenticated: false,
  signOut: async () => {}
};

// Create the context
const AuthContext = createContext<AuthState>(initialState);

// Provider component that wraps your app and makes auth object available to any child component
export function AuthProvider(t0) {
  const $ = _c(12);
  const {
    children,
    initialUser
  } = t0;
  const [user, setUser] = useState(initialUser ?? null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(initialUser === undefined);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = async () => {
      ;
      try {
        const supabase = createClient();
        await clearQueryCache();
        const {
          error: signOutError
        } = await supabase.auth.signOut();
        if (signOutError) {
          setError(signOutError);
        }
      } catch (t2) {
        const error_0 = t2;
        setError(error_0 instanceof Error ? error_0 : new Error(String(error_0)));
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const signOut = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  useEffect(_temp, t2);
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const supabase_0 = createClient();
      const {
        data: t5
      } = supabase_0.auth.onAuthStateChange((_event, session) => {
        setQueryCacheOwner(session?.user.id ?? null);
        syncRealtimeAuth(session).catch(_temp2);
        setUser(session?.user || null);
        setIsLoading(false);
      });
      const {
        subscription
      } = t5;
      return () => {
        subscription.unsubscribe();
      };
    };
    t4 = [];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  const t5 = !!user;
  let t6;
  if ($[4] !== error || $[5] !== isLoading || $[6] !== t5 || $[7] !== user) {
    t6 = {
      user,
      error,
      isLoading,
      isAuthenticated: t5,
      signOut
    };
    $[4] = error;
    $[5] = isLoading;
    $[6] = t5;
    $[7] = user;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const value = t6;
  let t7;
  if ($[9] !== children || $[10] !== value) {
    t7 = <AuthContext value={value}>{children}</AuthContext>;
    $[9] = children;
    $[10] = value;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}

// Custom hook to access the auth context
function _temp2(authError) {
  clientLogger.error("[AuthProvider] Error syncing realtime auth", authError);
}
function _temp() {
  if (typeof window === "undefined") {
    return;
  }
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get("fresh") === "1") {
    clientLogger.info("[AuthProvider] Fresh session detected, clearing all caches");
    clearAllCaches();
    urlParams.delete("fresh");
    const newSearch = urlParams.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "");
    window.history.replaceState({}, "", newUrl);
  }
}
export function useAuth() {
  const context = use(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}